import React from 'react';
import { Card, Row, Col, Tooltip, Grid } from 'antd';
import { 
  DollarOutlined, 
  FileDoneOutlined, 
  ClockCircleOutlined, 
  ExclamationCircleOutlined,
  CheckCircleOutlined,
  PercentageOutlined,
  CloseCircleOutlined,
  SyncOutlined
} from '@ant-design/icons';

const { useBreakpoint } = Grid;

const MetricDashboard = ({ homeData, tooltipTitle }:any) => {
  const screens = useBreakpoint();
  
  // Función para formatear moneda
  const moneda = (value:number) => {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS'
    }).format(value);
  };
  
  // Función para formatear porcentaje
  const porcentaje = (value:number) => {
    return `${(value * 100).toFixed(1)}%`;
  };

  // Configuración de métricas
  const metrics = [
    {
      key: 'generadas',
      title: 'Generadas',
      value: homeData?.generadasNuevas ?? '-',
      amount: moneda(homeData?.montoGeneradasNuevas ?? 0),
      tooltip: tooltipTitle?.[1] ?? 'Facturas generadas',
      icon: <FileDoneOutlined />,
      color: '#1890ff'
    },
    {
      key: 'regeneradas',
      title: 'Regeneradas',
      value: homeData?.regeneradas ?? '-',
      amount: moneda(homeData?.montoRegeneradas ?? 0),
      tooltip: tooltipTitle?.[10] ?? 'Facturas regeneradas',
      icon: <SyncOutlined />,
      color: '#13c2c2'
    },
    {
      key: 'pagadas',
      title: 'Pagas',
      value: homeData?.pagadas ?? '-',
      amount: moneda(homeData?.montoPagadas ?? 0),
      tooltip: tooltipTitle?.[2] ?? 'Facturas pagadas',
      icon: <CheckCircleOutlined />,
      color: '#52c41a'
    },
    {
      key: 'pagasPVoluntario',
      title: 'P. en P. Vol.',
      value: homeData?.pagasPVoluntario ?? '-',
      amount: moneda(homeData?.montoPagasPVoluntario ?? 0),
      tooltip: tooltipTitle?.[3] ?? 'Pendientes de pago voluntario',
      icon: <ClockCircleOutlined />,
      color: '#faad14'
    },
    {
      key: 'pagasPTotal',
      title: 'P. en P. Total',
      value: homeData?.pagasPTotal ?? '-',
      amount: moneda(homeData?.montoPagasPTotal ?? 0),
      tooltip: tooltipTitle?.[4] ?? 'Pendientes de pago total',
      icon: <ClockCircleOutlined />,
      color: '#fa8c16'
    },
    {
      key: 'impagas',
      title: 'Impagas',
      value: homeData?.impagas ?? '-',
      amount: moneda(homeData?.montoImpagas ?? 0),
      tooltip: tooltipTitle?.[5] ?? 'Facturas impagas',
      icon: <ExclamationCircleOutlined />,
      color: '#ff4d4f'
    },
    {
      key: 'impagasPVoluntario',
      title: 'I. en P. Vol.',
      value: homeData?.impagasPVoluntario ?? '-',
      amount: moneda(homeData?.montoImpagasPVoluntario ?? 0),
      tooltip: tooltipTitle?.[6] ?? 'Impagas pendientes voluntario',
      icon: <ClockCircleOutlined />,
      color: '#ff7a45'
    },
    {
      key: 'impagasPTotal',
      title: 'I. en P. Total',
      value: homeData?.impagasPTotal ?? '-',
      amount: moneda(homeData?.montoImpagasPTotal ?? 0),
      tooltip: tooltipTitle?.[7] ?? 'Impagas pendientes total',
      icon: <ClockCircleOutlined />,
      color: '#ff9c6e'
    },
    {
      key: 'vencidas',
      title: 'I. Venc.',
      value: homeData?.vencidas ?? '-',
      amount: moneda(homeData?.montoVencidas ?? 0),
      tooltip: tooltipTitle?.[8] ?? 'Facturas vencidas',
      icon: <CloseCircleOutlined />,//<ExclamationCircleOutlined />,
      color: '#cf1322'
    },
    {
      key: 'porcentajePagasGeneradas',
      title: 'Pagas/Generadas',
      value: porcentaje((homeData?.porcentajePagasGeneradas ?? 0) / 100),
      amount: '-',
      tooltip: tooltipTitle?.[9] ?? 'Porcentaje de pagas sobre generadas',
      icon: <PercentageOutlined />,
      color: '#722ed1'
    }
  ];

  // Determinar el número de columnas según el tamaño de pantalla
  const getColSpan = () => {
    if (screens.xxl) return 6; // 4 columnas en pantallas muy grandes
    if (screens.xl) return 6;  // 4 columnas
    if (screens.lg) return 8;  // 3 columnas
    if (screens.md) return 12; // 2 columnas
    return 24; // 1 columna en móviles
  };

  return (
    <div style={{ padding: screens.xs ? '8px' : '16px' }}>
      <Row gutter={[16, 16]}>
        {metrics.map((metric) => (
          <Col 
            xs={24} 
            sm={12} 
            md={12} 
            lg={8} 
            xl={6} 
            key={metric.key}
          >
            <Tooltip title={metric.tooltip}>
              <Card
                size="small"
                style={{
                  borderRadius: '12px',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.09)',
                  borderLeft: `4px solid ${metric.color}`,
                  height: '100%',
                  transition: 'all 0.3s ease'
                }}
                bodyStyle={{
                  padding: '16px'
                }}
                hoverable
              >
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: '12px' }}>
                  <div 
                    style={{
                      backgroundColor: `${metric.color}15`,
                      borderRadius: '8px',
                      padding: '8px',
                      marginRight: '12px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    {React.cloneElement(metric.icon, { 
                      style: { color: metric.color, fontSize: '18px' } 
                    })}
                  </div>
                  <span 
                    style={{ 
                      fontWeight: 600, 
                      color: '#2c3e50',
                      fontSize: screens.xs ? '14px' : '16px'
                    }}
                  >
                    {metric.title}
                  </span>
                </div>
                
                <div style={{ marginBottom: '8px' }}>
                  <div 
                    style={{ 
                      fontSize: screens.xs ? '18px' : '20px', 
                      fontWeight: 700,
                      color: metric.color
                    }}
                  >
                    {metric.value}
                  </div>
                </div>
                
                <div>
                  <div 
                    style={{ 
                      fontSize: screens.xs ? '14px' : '16px', 
                      color: '#666',
                      fontWeight: 500
                    }}
                  >
                    {metric.amount}
                  </div>
                </div>
              </Card>
            </Tooltip>
          </Col>
        ))}
      </Row>
    </div>
  );
};

export default MetricDashboard;